import { useCallback } from 'react'
import { Button } from '@highlight/ui'
import { Toast, useToast } from '@renderer/overlay/Toaster'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { useLiveMeetingIdentity } from '@renderer/overlay/Sidepanel/components/meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '@renderer/overlay/Sidepanel/components/meeting/meeting-view-id'
import { useOpenMeetingsSettings } from '@renderer/overlay/Sidepanel/utils/use-open-meetings-settings'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

interface AutopilotIntroToastProps {
  toastId: string
}

/**
 * Follows the first transcription a user starts from the post-onboarding
 * prompt. Autopilot stays on by default, so this is where they learn later
 * meetings start on their own and where to turn that off.
 */
export function AutopilotIntroToast({ toastId }: AutopilotIntroToastProps) {
  const toast = useToast()
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const liveMeeting = useLiveMeetingIdentity()
  const openMeetingsSettings = useOpenMeetingsSettings()

  const onOpenSettings = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      AmplitudeRenderer.trackEvent('Audio Note Toast', {
        type: 'autopilot intro',
        action: 'open settings',
      })
      openMeetingsSettings()
      toast.dismiss(toastId)
    },
    [openMeetingsSettings, toast, toastId],
  )

  const onOpenTranscript = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      openViewInSidepanel(MEETING_VIEW_ID, liveMeeting)
      toast.dismiss(toastId)
    },
    [openViewInSidepanel, liveMeeting, toast, toastId],
  )

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Description>
          From now on, Highlight starts transcribing as soon as it detects a meeting. You can change this in Settings.
        </Toast.Description>
        <Toast.Actions>
          <Button size="sm" variant="secondary" onClick={onOpenSettings}>
            Meeting Settings
          </Button>
          <Button size="sm" variant="primary" onClick={onOpenTranscript}>
            View Transcript
          </Button>
        </Toast.Actions>
      </Toast.Content>
    </Toast>
  )
}
